import { defineStore } from "pinia";
import { ref } from "vue";

export interface GameTeam {
  id: string | null;
  game: {
    id: string;
    win: boolean;
  } | null;
  placement: {
    id: string;
    place: number;
  } | null;
}

export interface Game {
  id: string;
  dateTime: string;
  teams: {
    home: GameTeam;
    away: GameTeam;
  };
  score: {
    home: number;
    away: number;
  } | null;
}

export const useGameStore = defineStore("game", () => {
  const games = ref<Game[]>([]);
  const loading = ref(false);
  const error = ref<null | string>(null);

  async function fetchAll() {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/games");
      if (!response.ok) {
        throw new Error("Failed to fetch games");
      }
      games.value = await response.json() as Game[];
    } catch (err) {
      error.value = "An error occurred while fetching games";
    } finally {
      loading.value = false;
    }
  }

  async function put(game: Partial<Game>) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/games", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(game),
      });
      if (!response.ok) {
        throw new Error("Failed to save game");
      }
      await fetchAll(); // Refresh the list after saving
    } catch (err) {
      error.value = "An error occurred while saving the game";
    } finally {
      loading.value = false;
    }
  }

  async function remove(game: Game) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`/api/games/${game.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete game");
      }
      await fetchAll(); // Refresh the list after deletion
    } catch (err) {
      error.value = "An error occurred while deleting the game";
    } finally {
      loading.value = false;
    }
  }

  // Initial fetch of games when the store is created
  fetchAll().then();

  return { games, loading, error, put, remove, fetchAll };
})
